import * as React from 'react';
import * as moment from 'moment';
import {Pdf} from '../../pdf';
import {BaseRouteProps} from '../../../interfaces';
import {Style, SurveyVersion, CharacteristicGroup, Characteristic, SurveyReportType} from '../../../models';
import {NotificationService} from '../../../services';
import './pdf_detailed.css';
import {PVAContainer, PVAStyle} from "../blocks";
import {
    FoundationsReportBottomContent,
    FoundationsReportLeftAndRightPair,
    FoundationsReportTeamContent,
    FoundationsReportTopContent,
    FoundationsReportDichotomiesContent,
    getNormalizedCompares
} from "../myreport";
import {CharacteristicBreakdown} from "../../inclinationmodals";


interface PdfDetailedProps extends Partial<BaseRouteProps<{}>> {
    selectedUserData: any;
    aboutSurveyText: string;
    surveyVersion: SurveyVersion;
    allStyles: Style[];
    notificationService: NotificationService;
    organizationName: string;
    surveyReportType: any;
    characteristicGroups?: { [id: number]: CharacteristicGroup };
    characteristics?: { [id: number]: Characteristic };
    surveyReportTypes?: SurveyReportType[];
}

export const exportDetailedPages = (props: PdfDetailedProps): React.ReactNode[] => {
    const { selectedUserData, aboutSurveyText, surveyVersion } = props;
    
    if (!selectedUserData || !selectedUserData.style) {
        return [];
    }

    let itemPageIndex = 3;
    const overviewContents = [
        { title: 'About the Assessment', page: itemPageIndex++ },
        { title: 'Your PVA Style', page: itemPageIndex++ },
        { title: 'Your PVA Style on a Team', page: itemPageIndex++ },
    ];

    // Check if we'll have Key Dichotomies page
    const hasKeyDichotomies = props.characteristicGroups && props.characteristics && props.surveyReportTypes;
    if (hasKeyDichotomies) {
        overviewContents.push({ title: 'Key Dichotomies', page: itemPageIndex++ });
    }
    
    const characteristicGroups = props.selectedUserData.inclinationGroups.filter((x:any) => {
        if (x.displayType !== 'normal') {
            return false;
        }
        if (!props.surveyReportType) {
            return true;
        }
        return x.surveyReportTypeID === props.surveyReportType.id;
    });

    let expandedCharacteristicGroups:any[] = [];
    characteristicGroups.forEach((group: any) => {
        if (group.data.length < 1 && group.subGroups && Array.isArray(group.subGroups)) {
            group.data = group.subGroups.map((subGroup: any) => subGroup.data).flat();
            group.subGroups = [];
        }
        expandedCharacteristicGroups.push(group);
    });

    let page = 1;
    let maxPage = itemPageIndex - 1 + expandedCharacteristicGroups.reduce((sum, group) => sum + (group.data ? group.data.length : 0), 0);
    
    let currentStyle = props.selectedUserData.style;
    if (props.allStyles && props.allStyles.length > 0) {
        const foundStyle = props.allStyles.find((style: Style) => {
            return style.name === props.selectedUserData.style.name;
        });
        if (foundStyle) {
            currentStyle = foundStyle;
        }
    }
    const badgeColor = currentStyle ? `var(--pva-${currentStyle.name.toLowerCase()})` : 'var(--blue)';

    // Calculate normalized compares for Key Dichotomies
    const normalizedCompares = getNormalizedCompares(
        selectedUserData,
        props.characteristicGroups || {},
        props.characteristics || {},
        props.surveyReportTypes || []
    );

    const styleHeader = <>
        <div className="style-info">
            <div className="title-content">
                <div className="subtitle">
                    Your PVA Style
                </div>
                <div className="title">
                    You are an {selectedUserData.style.name}
                </div>
            </div>
        </div>
        <PVAStyle
            notificationService={props.notificationService}
            history={props.history as any}
            location={props.location as any}
            match={props.match as any}
            style={props.selectedUserData.style}
            brief={props.selectedUserData.style.brief}
            content={props.selectedUserData.style.content}
            core={props.selectedUserData.style.core}
            name={props.selectedUserData.name}
            surveyVersion={props.surveyVersion}
            allStyles={props.allStyles}
            selectedUserData={props.selectedUserData}
            hideDichotomies={true}
            hideButtons={true}
            showTraits={true}
        />
    </>;

    const wrapPage = (content: React.ReactNode) => (
        <Pdf fontSize="8.5px" className="pdf-detailed">
            {content}
        </Pdf>
    );

    const pages: React.ReactNode[] = [];

    pages.push(wrapPage(
        <Pdf.TitlePage
            title="Pondera® Virtual Advisor"
            subtitle="Individual Report"
            name={selectedUserData.name}
            date={moment().format('MMMM D, YYYY')}
            organization={props.organizationName}
            currentPage={page++}
            totalPages={maxPage}
            preventInteraction={true}
        />
    ));

    pages.push(wrapPage(
        <Pdf.Page>
            <div className="table-of-contents">
                <div className="left-column">
                    <div className="subtitle">Pondera® Virtual Advisor</div>
                    <div className="title">Table of Contents</div>
                </div>
                <div className="right-column">
                    <div className="header-row">
                        <div className="overview-column">
                            OVERVIEW
                        </div>
                        <div className="page-column">
                            Page
                        </div>
                    </div>
                    {overviewContents.map((content, index) => (
                        <div key={index} className="contents-row">
                            <div className="overview-column">{content.title}</div>
                            <div className="dots-column"></div>
                            <div className="page-column">{content.page}</div>
                        </div>
                    ))}
                    
                    <div className="header-row">
                        Individual Report
                    </div>
        
                    {
                        expandedCharacteristicGroups.map((group:any, index:number) => (
                            <React.Fragment key={index}>
                                <div className="characteristic-group-header">
                                    <div className="domain">
                                        {group.subtitle}
                                    </div>
                                    <div className="name">
                                        {group.name}
                                    </div>
                                </div>
                                {group.data.map((characteristic: any, characteristicIndex: number) => (
                                    <div key={characteristicIndex} className="contents-row">
                                        <div className="overview-column">{characteristic.name}</div>
                                        <div className="dots-column"></div>
                                        <div className="page-column">{itemPageIndex++}</div>
                                    </div>
                                ))}
                            </React.Fragment>
                        ))
                    }
                </div>
            </div>
            
            <Pdf.Footer currentPage={page++} totalPages={maxPage} />
        </Pdf.Page>
    ));

    pages.push(wrapPage(
        <Pdf.Page>
            <div className="about-individual-report">
                <div className="title-content">
                    <div className="subtitle">
                        About the Assessment 
                    </div>
                    <div className="title">
                        Pondera® Virtual Advisor
                    </div>
                </div>
                <div dangerouslySetInnerHTML={{__html: aboutSurveyText}}/>
            </div>
            
            <Pdf.Footer currentPage={page++} totalPages={maxPage} />
        </Pdf.Page>
    ));

    pages.push(wrapPage(
        <Pdf.Page>
            { styleHeader }

            <FoundationsReportTopContent style={currentStyle} />
            <FoundationsReportLeftAndRightPair style={currentStyle} badgeColor={badgeColor} />
            <FoundationsReportBottomContent style={currentStyle} />
            
            <Pdf.Footer currentPage={page++} totalPages={maxPage} />
        </Pdf.Page>
    ));

    pages.push(wrapPage(
        <Pdf.Page>
            { styleHeader }

            <FoundationsReportTeamContent style={currentStyle} />

            <Pdf.Footer currentPage={page++} totalPages={maxPage} />
        </Pdf.Page>
    ));

    // Add Key Dichotomies page if data is available
    if (hasKeyDichotomies && normalizedCompares.length > 0) {
        pages.push(wrapPage(
            <Pdf.Page>
                <div className="style-info">
                    <div className="title-content">
                        <div className="subtitle">
                            Your PVA Style
                        </div>
                        <div className="title">
                            Key Dichotomies
                        </div>
                    </div>
                </div>

                <FoundationsReportDichotomiesContent normalizedCompares={normalizedCompares} badgeColor={badgeColor} />

                <Pdf.Footer currentPage={page++} totalPages={maxPage} />
            </Pdf.Page>
        ));
    }

    expandedCharacteristicGroups.forEach((group: any) => {
        group.data.forEach((characteristic: any) => {
            pages.push(wrapPage(
                <Pdf.Page key={`${group.name}-${characteristic.name}`} className={`characteristic-breakdown`}>
                    <div className="style-info">
                        <div className="title-content">
                            <div className="subtitle">
                                Individual Report
                            </div>
                            <div className="title">
                                {selectedUserData.name}
                            </div>
                        </div>
                    </div>
                    
                    <div className='breakdown-container'>
                        <PVAContainer id={group.subtitle+characteristic.name} topic={group.subtitle} title={group.name} preventInteraction={true}>
                            <CharacteristicBreakdown
                                name={characteristic.name}
                                description={characteristic.description}
                                value={characteristic.value}
                                content={characteristic.helpText}
                                preventInteraction={true}
                            />
                        </PVAContainer>
                    </div>
                    <Pdf.Footer currentPage={page++} totalPages={maxPage} />
                </Pdf.Page>
            ));
        });
    });

    return pages;
};

export class PdfDetailed extends React.Component<PdfDetailedProps> {
    public render() {
        const pages = exportDetailedPages(this.props);
        return <Pdf fontSize="8.5px" className="pdf-detailed">{pages}</Pdf>;
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/individual/pdf_detailed.tsx